(() => {
  const PAGE_SIZE = 18;

  const formatDate = (value) => {
    const date = new Date(`${value}T12:00:00Z`);
    if (Number.isNaN(date.getTime())) return value;

    return new Intl.DateTimeFormat(document.documentElement.lang || 'en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(date);
  };

  const normalize = (value) => String(value || '').trim().toLocaleLowerCase();

  const createReviewCard = (review) => {
    const item = document.createElement('li');
    item.className = 'aqk-review-index__item';

    const article = document.createElement('article');
    article.className = 'aqk-review-index-card';

    const topline = document.createElement('div');
    topline.className = 'aqk-review-index-card__topline';

    const stars = document.createElement('span');
    stars.className = 'aqk-review-index-card__stars';
    stars.setAttribute('role', 'img');
    stars.setAttribute('aria-label', `${review.rating} out of 5 stars`);
    for (let index = 1; index <= 5; index += 1) {
      const star = document.createElement('span');
      star.setAttribute('aria-hidden', 'true');
      star.textContent = '★';
      if (index > review.rating) star.className = 'is-empty';
      stars.append(star);
    }

    const rating = document.createElement('span');
    rating.className = 'aqk-review-index-card__rating';
    rating.textContent = `${review.rating}.0`;

    const title = document.createElement('h2');
    title.textContent = review.title;

    const quote = document.createElement('blockquote');
    const body = document.createElement('p');
    body.textContent = review.body;
    quote.append(body);

    const footer = document.createElement('footer');
    const avatar = document.createElement('span');
    avatar.className = 'aqk-review-index-card__avatar';
    avatar.setAttribute('aria-hidden', 'true');
    avatar.textContent = review.reviewer.slice(0, 1).toLocaleUpperCase();

    const person = document.createElement('span');
    person.className = 'aqk-review-index-card__person';
    const name = document.createElement('strong');
    name.textContent = review.reviewer;
    const label = document.createElement('small');
    label.textContent = 'Customer review';
    person.append(name, label);

    const time = document.createElement('time');
    time.dateTime = review.date;
    time.textContent = formatDate(review.date);

    topline.append(stars, rating);
    footer.append(avatar, person, time);
    article.append(topline, title, quote, footer);
    item.append(article);

    return item;
  };

  const initializeReviewIndex = (root) => {
    if (root.dataset.aqkInitialized === 'true') return;

    const dataset = window.AQK_REVIEWS_DATA;
    const reviews = Array.isArray(dataset?.reviews) ? dataset.reviews : [];
    const grid = root.querySelector('[data-aqk-review-grid]');
    const search = root.querySelector('[data-aqk-review-search]');
    const sort = root.querySelector('[data-aqk-review-sort]');
    const resultStatus = root.querySelector('[data-aqk-review-status]');
    const loading = root.querySelector('[data-aqk-review-loading]');
    const empty = root.querySelector('[data-aqk-review-empty]');
    const loadMore = root.querySelector('[data-aqk-review-load-more]');
    const filterButtons = [...root.querySelectorAll('[data-aqk-review-filter]')];

    if (!reviews.length || !grid || !search || !sort || !resultStatus || !loadMore) {
      if (loading) loading.textContent = 'Reviews could not be loaded. Please refresh the page.';
      return;
    }

    root.dataset.aqkInitialized = 'true';
    const state = { rating: 'all', query: '', sort: 'newest', visible: PAGE_SIZE };

    const getFilteredReviews = () => {
      const filtered = reviews.filter((review) => {
        const matchesRating = state.rating === 'all' || review.rating === Number(state.rating);
        const haystack = normalize(`${review.title} ${review.body} ${review.reviewer}`);
        return matchesRating && (!state.query || haystack.includes(state.query));
      });

      return filtered.sort((first, second) => {
        if (state.sort === 'oldest') return first.date.localeCompare(second.date);
        if (state.sort === 'highest') return second.rating - first.rating || second.date.localeCompare(first.date);
        if (state.sort === 'lowest') return first.rating - second.rating || second.date.localeCompare(first.date);
        return second.date.localeCompare(first.date);
      });
    };

    const render = () => {
      const filtered = getFilteredReviews();
      const visibleReviews = filtered.slice(0, state.visible);
      const fragment = document.createDocumentFragment();

      visibleReviews.forEach((review) => fragment.append(createReviewCard(review)));
      grid.replaceChildren(fragment);

      const shown = visibleReviews.length;
      const total = filtered.length;
      resultStatus.textContent = total
        ? `Showing ${shown} of ${total} ${total === 1 ? 'review' : 'reviews'}`
        : 'No reviews match your search';

      if (empty) empty.hidden = total !== 0;
      loadMore.hidden = shown >= total;
      loadMore.textContent = `Show more reviews (${total - shown} remaining)`;

      filterButtons.forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.aqkReviewFilter === state.rating));
      });

      if (loading) loading.hidden = true;
    };

    const setRating = (rating) => {
      state.rating = rating;
      state.visible = PAGE_SIZE;
      render();
    };

    let searchTimer;
    search.addEventListener('input', () => {
      window.clearTimeout(searchTimer);
      searchTimer = window.setTimeout(() => {
        state.query = normalize(search.value);
        state.visible = PAGE_SIZE;
        render();
      }, 160);
    });

    sort.addEventListener('change', () => {
      state.sort = sort.value;
      state.visible = PAGE_SIZE;
      render();
    });

    root.addEventListener('click', (event) => {
      const filter = event.target.closest('[data-aqk-review-filter]');
      const distribution = event.target.closest('[data-aqk-review-distribution]');

      if (filter) setRating(filter.dataset.aqkReviewFilter);
      if (distribution) {
        setRating(distribution.dataset.aqkReviewDistribution);
        root.querySelector('[data-aqk-review-controls]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });

    loadMore.addEventListener('click', () => {
      const previousCount = grid.children.length;
      state.visible += PAGE_SIZE;
      render();
      grid.children[previousCount]?.querySelector('h2')?.setAttribute('tabindex', '-1');
      grid.children[previousCount]?.querySelector('h2')?.focus({ preventScroll: true });
    });

    render();
  };

  const initialize = () => {
    document.querySelectorAll('[data-aqk-review-index]').forEach(initializeReviewIndex);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', initialize);
})();
